import React, { useState, useMemo, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import deleteIcon from '../../../../../../Assets/images/delete-icon.svg';
import filterIcon from '../../../../../../Assets/images/filter-icon.svg';
import clearIcon from '../../../../../../Assets/images/clear-icon.svg';
import editIcon from "../../../../../../Assets/images/edit-icon.svg";
import plusIcon from '../../../../../../Assets/images/plus-icon.svg';
import calendarIcon from "../../../../../../Assets/images/calender.svg";
import closeRedIcon from "../../../../../../Assets/images/close-red.svg";

import AddUserModal from '../AddUserModal/AddUserModal';
import EditUserModal from '../EditUserModal/EditUserModal';
import ClipLoader from "react-spinners/ClipLoader";
import ConfirmationModal from '../../../../../../Components/ConfirmationBox/ConfirmationModal';
import apiClient from '../../../../../../Config/apiClient';
import { showErrorToast, showSuccessToast } from '../../../../../../Context/Toaster/Toaster';
import { DATE_FORMAT, DATE_MONTH_FORMAT } from '../../../Utils/Constants';

const siteUserPath = process.env.REACT_APP_CC_SITE_USERS_BASE_PATH;
const rolesPath = process.env.REACT_APP_CC_ROLES_BASE_PATH;

const UserList = ({ users, loading, refreshUserList, canEditUser }) => {
    const [usersPerPage, setUsersPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [editUser, setEditUser] = useState(null);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);

    const [filterRoles, setFilterRoles] = useState([]);
    const [filterRoleId, setFilterRoleId] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const filterRef = useRef(null);
    const [filtersApplied, setFiltersApplied] = useState(false);

    useEffect(() => {
        apiClient.get(`${rolesPath}/list`)
            .then(res => {
                if (res.data.status === 'SUCCESS') {
                    setFilterRoles(res.data.data);
                }
            })
            .catch(() => showErrorToast("Failed to fetch roles"));
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setShowFilterDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const totalUsers = users.length;
    const totalPages = Math.ceil(totalUsers / usersPerPage);
    const startIndex = (currentPage - 1) * usersPerPage;

    const currentUsers = useMemo(() =>
        users.slice(startIndex, startIndex + usersPerPage),
        [users, startIndex, usersPerPage]
    );

    const selectAllChecked = useMemo(() =>
        currentUsers.length > 0 && currentUsers.every(user => selectedUserIds.includes(user.id)),
        [selectedUserIds, currentUsers]
    );

    const applyFilters = () => {
        const getUTCDateTime = (dateStr, isEnd = false) => {
            if (!dateStr) return undefined;
            const localDate = new Date(`${dateStr}T${isEnd ? '23:59:59' : '00:00:00'}`);
            return localDate.toISOString();
        };

        const payload = {
            role_id: filterRoleId || undefined,
            start_date: getUTCDateTime(filterStartDate),
            end_date: getUTCDateTime(filterEndDate, true),
        };

        apiClient.post(`${siteUserPath}/list`, payload)
            .then(res => {
                if (res.data.status === 'SUCCESS') {
                    refreshUserList(res.data.data);
                    setCurrentPage(1);
                    setFiltersApplied(!!(filterRoleId || filterStartDate || filterEndDate));
                }
            })
            .catch(() => showErrorToast("Failed to apply filters"))
            .finally(() => setShowFilterDropdown(false));
    };

    const clearFilters = (e) => {
        e?.preventDefault();
        setFilterRoleId('');
        setFilterStartDate('');
        setFilterEndDate('');
        setFiltersApplied(false);

        apiClient.post(`${siteUserPath}/list`, {})
            .then(res => {
                if (res.data.status === 'SUCCESS') {
                    refreshUserList(res.data.data);
                    setCurrentPage(1);
                }
            })
            .catch(() => showErrorToast("Failed to fetch users"))
    };

    const isAnyFilterApplied = filterRoleId || filterStartDate || filterEndDate;
    const handlePageChange = (p) => p >= 1 && p <= totalPages && setCurrentPage(p);

    const handleSelectAllChange = (e) => {
        const ids = currentUsers.map(u => u.id);
        setSelectedUserIds(prev =>
            e.target.checked ? [...new Set([...prev, ...ids])] : prev.filter(id => !ids.includes(id))
        );
    };

    const handleUserCheckbox = (id) => {
        setSelectedUserIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleDeleteUsers = async () => {
        try {
            const res = await apiClient.put(`${siteUserPath}/delete`, { user_ids: selectedUserIds });
            if (res.data.status === 'SUCCESS') {
                showSuccessToast(res.data.message || "Users deleted successfully");
                setSelectedUserIds([]);
                refreshUserList();
            } else {
                showErrorToast(res.data.message || "Failed to delete users");
            }
        } catch {
            showErrorToast("Error deleting users");
        } finally {
            setShowDeleteConfirm(false);
        }
    };

    return (
        <main className="full-wraper">
            <div className="main-wraper">
                <div className="container-fluid">
                    <div className="main-inner">
                        <div className="common-top">
                            {canEditUser && (
                                <a href="#" onClick={e => { e.preventDefault(); if (selectedUserIds.length) setShowDeleteConfirm(true); }} className={selectedUserIds.length === 0 ? 'disabled-delete' : ''} style={{ pointerEvents: selectedUserIds.length === 0 ? 'none' : 'auto', opacity: selectedUserIds.length === 0 ? 0.5 : 1 }}>
                                    <img className="ct-icon" src={deleteIcon} alt="Delete" />Delete
                                </a>
                            )}
                            <div className="filter-cvr" ref={filterRef}>
                                <a className="filter-btn" href="#" onClick={e => { e.preventDefault(); setShowFilterDropdown(prev => !prev); }}>
                                    <span className="filter-icon-wrapper">
                                        <img className="ct-icon" src={filterIcon} alt="Filter" />
                                        {filtersApplied && <span className="filter-indicator-dot"></span>}
                                    </span>
                                    Filter
                                </a>
                                <a className="clear-btn" href="#" onClick={clearFilters}>
                                    <img className="ct-icon" src={clearIcon} alt="Clear" />Clear Filter
                                </a>

                                {showFilterDropdown && (
                                    <ul className="dropdown-menu filterdrop-body show">
                                        <div className="filterdrop-split">
                                            <label className="filter-label">Permission
                                                {filterRoleId && <img className="red-close" src={closeRedIcon} alt="clear" onClick={() => setFilterRoleId('')} />}
                                            </label>
                                            <select className="filter-select" value={filterRoleId} onChange={e => setFilterRoleId(e.target.value)}>
                                                <option value="">--Select--</option>
                                                {filterRoles.map(role => (
                                                    <option key={role.id} value={role.id}>{role.role_name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="filterdrop-split">
                                            <label className="filter-label">Created Date
                                                {(filterStartDate || filterEndDate) && (
                                                    <img className="red-close" src={closeRedIcon} alt="Clear" onClick={() => { setFilterStartDate(''); setFilterEndDate(''); }} />
                                                )}
                                            </label>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="filter-datepicker from-date">
                                                    <DatePicker
                                                        selected={filterStartDate ? new Date(filterStartDate) : null}
                                                        onChange={(date) => {
                                                            const formatted = date?.toLocaleDateString('en-CA');
                                                            setFilterStartDate(formatted);
                                                        }}
                                                        placeholderText="Start Date"
                                                        className="filter-date"
                                                        dateFormat={DATE_MONTH_FORMAT}
                                                    />
                                                    <span className="filter-calendar-icon"><img src={calendarIcon} alt="Calendar" /></span>
                                                </div>
                                                <div className="filter-datepicker to-date">
                                                    <DatePicker
                                                        selected={filterEndDate ? new Date(filterEndDate) : null}
                                                        onChange={(date) => {
                                                            const formatted = date?.toLocaleDateString('en-CA');
                                                            setFilterEndDate(formatted);
                                                        }}
                                                        placeholderText="End Date"
                                                        className="filter-date"
                                                        dateFormat={DATE_MONTH_FORMAT}
                                                    />
                                                    <span className="filter-calendar-icon"><img src={calendarIcon} alt="Calendar" /></span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="filterdrop-split mb-0">
                                            <div className="filter-buttons">
                                                <a href="#" className="filter-clear" onClick={clearFilters}>Clear</a>
                                                <a href="#" className="filter-apply" onClick={e => { e.preventDefault(); applyFilters(); }}>Apply</a>
                                            </div>
                                        </div>
                                    </ul>
                                )}
                            </div>

                            {canEditUser && (
                                <a className='post-button' href="#" data-bs-toggle="modal" data-bs-target="#addUserModal">
                                    {/* <img src={plusIcon} alt="Add" /> */}
                                    Add User
                                </a>
                            )}
                        </div>

                        <div className="table-cvr">
                            <div className="table-inner">
                                <table className="table" id="roleTable">
                                    <thead>
                                        <tr>
                                            <th><input type="checkbox" className="common-check table-checkbox" checked={selectAllChecked} onChange={handleSelectAllChange} /></th>
                                            <th>#</th>
                                            <th>First Name</th>
                                            <th>Last Name</th>
                                            <th>Email</th>
                                            <th>Contact No.</th>
                                            <th>Permission</th>
                                            <th>Created</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="9" className="text-center py-4"><ClipLoader size={30} color="gray" /></td></tr>
                                        ) : currentUsers.length > 0 ? (
                                            currentUsers.map((user, index) => (
                                                <tr key={user.id}>
                                                    <td><input type="checkbox" className="common-check table-checkbox" checked={selectedUserIds.includes(user.id)} onChange={() => handleUserCheckbox(user.id)} /></td>
                                                    <td>{startIndex + index + 1}</td>
                                                    <td><a className="table-link" href="#" onClick={e => { e.preventDefault(); setEditUser(user); }} data-bs-toggle="modal" data-bs-target="#editUserModal">{user.first_name}</a></td>
                                                    <td>{user.last_name}</td>
                                                    <td>{user.email_id}</td>
                                                    <td>{user.contact_no}</td>
                                                    <td>{user.role?.role_name}</td>
                                                    <td>{moment(user.created_date_time).local().format(DATE_FORMAT)}</td>
                                                    <td>
                                                        {canEditUser && (
                                                            <a href="#" onClick={e => { e.preventDefault(); setEditUser({ ...user, openInEditMode: true }); }} data-bs-toggle="modal" data-bs-target="#editUserModal">
                                                                <img className="tb-icon" src={editIcon} alt="Edit" />
                                                            </a>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="9" className="text-center py-4 text-muted">No users found.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="dt-layout-row d-flex align-items-center justify-content-between mt-3">
                                <div className="dt-length">
                                    Showing{' '}
                                    <select className="dt-input" value={usersPerPage} onChange={e => { setCurrentPage(1); setUsersPerPage(+e.target.value); }}>
                                        {[5, 10, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>{' '}
                                    of {totalUsers} entries
                                </div>
                                <div className="dt-paging d-flex align-items-center" style={{ gap: '5px' }}>
                                    <button className="dt-paging-button previous" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                                    {Array.from({ length: totalPages }).map((_, i) => (
                                        <button key={i + 1} className={`dt-paging-button ${currentPage === i + 1 ? 'current' : ''}`} onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                                    ))}
                                    <button className="dt-paging-button next" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                                </div>
                            </div>
                        </div>

                        <AddUserModal refreshUserList={refreshUserList} />
                        <EditUserModal user={editUser} refreshUserList={refreshUserList} canEditUser={canEditUser} />
                        <ConfirmationModal
                            id="deleteConfirmModal"
                            show={showDeleteConfirm}
                            title="Delete Users"
                            message={`Are you sure you want to delete ${selectedUserIds.length} user(s)?`}
                            confirmText="Yes"
                            cancelText="Cancel"
                            onConfirm={handleDeleteUsers}
                            onCancel={() => setShowDeleteConfirm(false)}
                        />
                    </div>
                </div>
            </div>
        </main>
    );
};

export default UserList;